<template>
  <section>
    <v-progress-linear
      v-if="vehicleSelectionState.isLoadingEvent || vehicleSelectionState.isLoadingVehicles"
      indeterminate
    />

    <AppPageTitleBar
      :title="route.meta.title"
      :back-to="vehicleListRoute"
      back-label="Natrag"
    />

    <form
      class="form form--with-sticky-actions page-stack grid-stack grid-gap-page-section"
      @submit.prevent="saveVehicleSelectionAsync"
    >
      <section class="page-panel page-panel--mobile-unframed grid-stack grid-gap-20">
        <Filters
          v-model="vehicleSelectionState.filters"
          :filter-fields="vehicleFilterFields"
          @change="getVehiclesAsync(1)"
        />

        <v-table class="table vehicle-selection-table">
          <thead class="text-mid">
            <tr>
              <th class="vehicle-selection-table__checkbox" />
              <th>Marka</th>
              <th>Model</th>
              <th>Registarska oznaka</th>
              <th class="vehicle-selection-table__chassis">Broj šasije</th>
              <th>Boja</th>
              <th>Status za vrijeme događaja</th>
            </tr>
          </thead>

          <tbody class="text-mid">
            <tr
              v-for="vehicle in vehicleSelectionState.vehicles"
              :key="vehicle.id"
              :class="{ 'vehicle-selection-table__row--non-active': vehicle.selectionStatus !== 'active' }"
            >
              <td>
                <v-checkbox
                  v-model="vehicleSelectionState.selectedVehicleIds"
                  :aria-label="`Odaberi ${vehicle.brand} ${vehicle.model}, ${vehicle.registrationPlate}`"
                  class="checkbox"
                  density="compact"
                  :disabled="!vehicleSelectionState.eventDetail || isEventArchived || !vehicle.isSelectable"
                  hide-details
                  multiple
                  :value="vehicle.id"
                />
              </td>
              <td>{{ vehicle.brand }}</td>
              <td>{{ vehicle.model }}</td>
              <td>{{ vehicle.registrationPlate }}</td>
              <td>{{ vehicle.chassisNumber }}</td>
              <td>{{ vehicle.color }}</td>
              <td>
                <v-chip
                  :class="`status-chip status-chip--${vehicle.selectionStatus}`"
                  label
                  variant="flat"
                >
                  {{ vehicle.selectionStatusLabel }}
                </v-chip>
              </td>
            </tr>

            <tr v-if="!vehicleSelectionState.isLoadingVehicles && !vehicleSelectionState.vehicles.length">
              <td
                class="table__empty"
                colspan="7"
              >
                Nema vozila koja odgovaraju pretraživanju.
              </td>
            </tr>
          </tbody>
        </v-table>

        <Pagination
          :page="vehicleSelectionState.currentPage"
          :per-page="vehiclesPerPage"
          :total-results="vehicleSelectionState.totalResults"
          @page-change="getVehiclesAsync"
        />
      </section>

      <div class="submit-bar">
        <ToolbarActionButton
          class="vehicle-selection-submit"
          type="submit"
          :disabled="vehicleSelectionState.isLoadingEvent || isEventArchived || vehicleSelectionState.isSaving"
          :loading="vehicleSelectionState.isSaving"
          :icon="null"
        >
          Prihvati
          <span class="vehicle-selection-submit__count">
            {{ vehicleSelectionState.selectedVehicleIds.length }}
          </span>
        </ToolbarActionButton>
      </div>
    </form>
  </section>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import Filters from '../../components/Filters.vue'
import Pagination from '../../components/Pagination.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { canAccessBrand } from '../../auth/authorization'
import { getEventBrandName, getVehicleBrandOptions } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
const vehiclesPerPage = 10
let latestEventRequestId = 0
let latestVehiclesRequestId = 0

const vehicleSelectionStatusLabels = {
  active: 'Aktivno',
  occupied: 'Zauzeto',
  defective: 'Neispravno',
}

const vehicleFilterFields = [
  {
    key: 'search',
    type: 'search',
    placeholder: 'Pretraži po modelu, registracijskoj oznaci ili broju šasije...',
    grow: true,
  },
  {
    key: 'brandCode',
    type: 'select',
    placeholder: 'MARKA',
    items: getVehicleBrandOptions().filter((brandOption) => canAccessBrand(brandOption.value)),
    width: '230px',
  },
]

const vehicleSelectionState = reactive({
  filters: {
    search: null,
    brandCode: null,
  },
  currentPage: 1,
  totalResults: 0,
  vehicles: [],
  eventDetail: null,
  eventVehicleIds: [],
  selectedVehicleIds: [],
  isLoadingEvent: false,
  isLoadingVehicles: false,
  isSaving: false,
})

const eventId = computed(() => route.params.eventId)
const isEventArchived = computed(() => vehicleSelectionState.eventDetail?.isArchived ?? false)
const vehicleListRoute = computed(() => ({
  name: 'event-detail-vehicles',
  params: { eventId: eventId.value },
}))

watch(eventId, loadVehicleSelectionAsync, { immediate: true })

async function loadVehicleSelectionAsync () {
  const requestId = ++latestEventRequestId

  latestVehiclesRequestId++
  vehicleSelectionState.filters.search = null
  vehicleSelectionState.filters.brandCode = null
  vehicleSelectionState.currentPage = 1
  vehicleSelectionState.totalResults = 0
  vehicleSelectionState.vehicles = []
  vehicleSelectionState.eventDetail = null
  vehicleSelectionState.eventVehicleIds = []
  vehicleSelectionState.selectedVehicleIds = []
  vehicleSelectionState.isLoadingEvent = true
  vehicleSelectionState.isLoadingVehicles = false

  try {
    const event = await apiClient.getAsync(`/api/events/${eventId.value}`)

    if (requestId !== latestEventRequestId) {
      return
    }

    vehicleSelectionState.eventDetail = event
    vehicleSelectionState.eventVehicleIds = event.vehicles.map(({ id }) => id)
    vehicleSelectionState.selectedVehicleIds = [...vehicleSelectionState.eventVehicleIds]

    await getVehiclesAsync(1)
  } catch (error) {
    console.error('Failed to load the event for vehicle selection.', error)

    if (requestId === latestEventRequestId) {
      showRequestErrorToast(error, 'Događaj nije bilo moguće učitati.')
    }
  } finally {
    if (requestId === latestEventRequestId) {
      vehicleSelectionState.isLoadingEvent = false
    }
  }
}

async function getVehiclesAsync (page = 1) {
  const requestId = ++latestVehiclesRequestId

  vehicleSelectionState.isLoadingVehicles = true

  try {
    const vehicleListResponse = await apiClient.getAsync('/api/vehicles', {
      queryParams: {
        brandCode: vehicleSelectionState.filters.brandCode,
        eventId: eventId.value,
        page,
        perPage: vehiclesPerPage,
        search: vehicleSelectionState.filters.search?.trim() || null,
      },
    })

    if (requestId !== latestVehiclesRequestId) {
      return
    }

    vehicleSelectionState.vehicles = vehicleListResponse.results
      .map((vehicle) => {
        const selectionStatus = !vehicle.isActive
          ? 'defective'
          : vehicle.isOccupied ? 'occupied' : 'active'

        return {
          id: vehicle.id,
          brand: getEventBrandName(vehicle.brandCode),
          model: vehicle.name,
          registrationPlate: vehicle.plate,
          chassisNumber: vehicle.vin,
          color: vehicle.color,
          isSelectable: vehicleSelectionState.eventVehicleIds.includes(vehicle.id) || selectionStatus === 'active',
          selectionStatus,
          selectionStatusLabel: vehicleSelectionStatusLabels[selectionStatus],
        }
      })
    vehicleSelectionState.currentPage = vehicleListResponse.pagination.page
    vehicleSelectionState.totalResults = vehicleListResponse.pagination.totalResults
  } catch (error) {
    console.error('Failed to load vehicles for event selection.', error)

    if (requestId === latestVehiclesRequestId) {
      vehicleSelectionState.vehicles = []
      vehicleSelectionState.totalResults = 0
      showRequestErrorToast(error, 'Vozila nije bilo moguće učitati.')
    }
  } finally {
    if (requestId === latestVehiclesRequestId) {
      vehicleSelectionState.isLoadingVehicles = false
    }
  }
}

async function saveVehicleSelectionAsync () {
  if (
    vehicleSelectionState.isLoadingEvent ||
    vehicleSelectionState.isSaving ||
    !vehicleSelectionState.eventDetail ||
    isEventArchived.value
  ) {
    return
  }

  vehicleSelectionState.isSaving = true

  try {
    await apiClient.postAsync(
      `/api/events/${eventId.value}/vehicles`,
      vehicleSelectionState.selectedVehicleIds
    )

    showSuccessToast('Vozila su uspješno spremljena.')
    await router.push(vehicleListRoute.value)
  } catch (error) {
    console.error('Failed to save event vehicles.', error)
    showRequestErrorToast(error, 'Vozila nije bilo moguće spremiti.')
  } finally {
    vehicleSelectionState.isSaving = false
  }
}
</script>

<style
  scoped
  lang="scss"
>
.vehicle-selection-table {
  --table-min-width: 900px;
  --table-actions-width: 18%;

  &__checkbox {
    width: 44px;
  }

  &__chassis {
    width: 20%;
  }

  &__row--non-active {
    background: #f9f7f7;
  }
}

.vehicle-selection-submit__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  margin-left: 8px;
  border-radius: 50%;
  background: var(--color-text-muted);
  font-size: 12px;
}
</style>
